// features/projects/index.js — PROJECTS FEATURE ROUTER
// ═══════════════════════════════════════════════════════════
// Self-contained Projects feature. Mirrors WorkstationsFeature
// pattern: this file holds the top-level state (panel vs
// workstation) and routes between them.
//
// Two views:
//   1. ProjectsPanel       — card grid (Active | Finished)
//   2. ProjectWorkstation  — full 3-panel chat for an opened project
//
// Per Tims architecture rule: chat is the main driver and must
// be isolated. ProjectWorkstation + ProjectChatPanel + useProjectChat
// are all forks of the Solo equivalents — changes here cannot affect
// Solo workspace and vice versa.
// ═══════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';
import ProjectsPanel from './projects-panel';
import ProjectWorkstation from './project-workstation';
import ContinuumWorkstation from './templates/continuum';
import ProfessionalWorkstation from './templates/professional';

// Supabase client for opening a project's workspace row.
// We instantiate locally (the parent passes its own supabase via prop)
// to keep this feature fully self-contained.
const supabase = createClient(
  process.env.NEXT_PUBLIC_SUPABASE_URL,
  process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY,
);

export default function ProjectsFeature({
  userId,
  userRecord,
  voiceConfig,
  onClose,
  onSidebarCollapse,
}) {
  // null = on Panel. object = inside the workstation for that project.
  // Lazy init from localStorage so refresh inside the chat keeps the
  // user in the chat instead of bouncing back to the panel. (Previously
  // a separate restore-on-mount effect raced with the persist effect:
  // the persist effect ran first with the initial null state and
  // wiped the saved key before the restore effect could read it.)
  const [activeWorkspaceSession, setActiveWorkspaceSession] = useState(() => {
    if (typeof window === 'undefined') return null;
    const saved = window.localStorage.getItem('shift_active_project_workspace');
    if (!saved) return null;
    try { return JSON.parse(saved); } catch {
      window.localStorage.removeItem('shift_active_project_workspace');
      return null;
    }
  });

  // Persist active workspace across refreshes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (activeWorkspaceSession) {
      window.localStorage.setItem(
        'shift_active_project_workspace',
        JSON.stringify(activeWorkspaceSession),
      );
      onSidebarCollapse?.(true);
    } else {
      window.localStorage.removeItem('shift_active_project_workspace');
    }
  }, [activeWorkspaceSession, onSidebarCollapse]);

  const handleOpenProject = useCallback(async (project) => {
    const workspaceId = project?.intake?.workspace_id;
    if (!workspaceId) {
      console.warn('[projects] no workspace_id on project', project?.id);
      return;
    }
    try {
      const { data: ws, error } = await supabase
        .from('shared_spaces')
        .select('*')
        .eq('id', workspaceId)
        .single();
      if (error || !ws) {
        console.warn('[projects] workspace lookup failed', workspaceId, error?.message);
        return;
      }
      setActiveWorkspaceSession({
        ...ws,
        participants: [userId],
        others: [],
      });
    } catch (err) {
      console.warn('[projects] open failed:', err.message);
    }
  }, [userId]);

  const handleExitWorkstation = useCallback(() => {
    setActiveWorkspaceSession(null);
    onSidebarCollapse?.(false);
  }, [onSidebarCollapse]);

  if (activeWorkspaceSession) {
    // Route by template — the template id was stamped into
    // workspace_metadata at project creation time. Standard projects
    // (and any unknown template) fall back to the existing
    // ProjectWorkstation. Named templates render their own shell.
    const template = activeWorkspaceSession?.workspace_metadata?.template
      || 'standard';

    if (template === 'continuum') {
      return (
        <ContinuumWorkstation
          userId={userId}
          userRecord={userRecord}
          supabase={supabase}
          workspaceSession={activeWorkspaceSession}
          voiceConfig={voiceConfig}
          onExit={handleExitWorkstation}
          onSidebarCollapse={onSidebarCollapse}
        />
      );
    }

    if (template === 'professional') {
      return (
        <ProfessionalWorkstation
          userId={userId}
          userRecord={userRecord}
          supabase={supabase}
          workspaceSession={activeWorkspaceSession}
          voiceConfig={voiceConfig}
          onExit={handleExitWorkstation}
          onSidebarCollapse={onSidebarCollapse}
        />
      );
    }

    return (
      <ProjectWorkstation
        userId={userId}
        userRecord={userRecord}
        supabase={supabase}
        workspaceSession={activeWorkspaceSession}
        voiceConfig={voiceConfig}
        onExit={handleExitWorkstation}
        onSidebarCollapse={onSidebarCollapse}
      />
    );
  }

  return (
    <ProjectsPanel
      userId={userId}
      userRecord={userRecord}
      onClose={onClose}
      onOpenProject={handleOpenProject}
    />
  );
}
